import { apiDownload, apiRequest } from './client.js';

/**
 * Tenant invoice self-service (SDD §2.3, Phase D-2). The API has existed since
 * D-2 and is exempt from the read-only write-lock ("a read-only tenant must still
 * see and pay its bills"); ADR-0032 PR-2 is its first front-end consumer. Mirrors
 * the API's formatTenantInvoice serializer (apps/api/src/services/invoiceFormat.ts).
 * Drafts are never returned — they are operator-internal.
 */

export type TenantInvoiceStatus = 'issued' | 'paid' | 'overdue' | 'voided';

export interface TenantInvoice {
  id: string;
  number: string;
  status: TenantInvoiceStatus;
  currency: string;
  subtotalAmount: string; // Decimal(18,4) as a string
  taxAmount: string;
  totalAmount: string;
  lineItems: { description: string; quantity: number; unitAmount: number; totalAmount: number }[];
  notes: string | null;
  /** ADR-0032 D2 — the period this invoice pays for (null = not a renewal). */
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  dueDate: string;
  issuedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  createdAt: string;
}

/** GET /v1/invoices/me — the tenant's own non-draft invoices, newest first. */
export function getMyInvoices(): Promise<{ invoices: TenantInvoice[] }> {
  return apiRequest<{ invoices: TenantInvoice[] }>('/v1/invoices/me');
}

/**
 * GET /v1/invoices/me/:id/pdf — authenticated download. A save rather than a
 * new tab: `window.open` after an await is popup-blocked in most browsers, and
 * a raw href would depend on the refresh cookie riding a navigation.
 */
export function downloadMyInvoicePdf(invoice: Pick<TenantInvoice, 'id' | 'number'>): Promise<void> {
  return apiDownload(`/v1/invoices/me/${invoice.id}/pdf`, `${invoice.number}.pdf`);
}
